import axios from 'axios';
import { refreshToken } from './auth';

const request = axios.create({
  baseURL: '/api',
  timeout: 60000 // 增加到60秒，因为大模型API测试可能需要更长时间
});

// Token存储key
const TOKEN_KEY = 'auth_token';
const REFRESH_TOKEN_KEY = 'auth_refresh_token';
const USER_INFO_KEY = 'auth_user_info';

/**
 * 获取存储的Token
 */
export function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}

/**
 * 获取存储的刷新Token
 */
export function getRefreshToken() {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
}

/**
 * 设置Token
 */
export function setToken(token, refreshToken, userInfo) {
  localStorage.setItem(TOKEN_KEY, token);
  if (refreshToken) {
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  }
  if (userInfo) {
    localStorage.setItem(USER_INFO_KEY, JSON.stringify(userInfo));
  }
}

/**
 * 清除Token
 */
export function clearToken() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(USER_INFO_KEY);
}

/**
 * 获取用户信息
 */
export function getUserInfo() {
  const userInfoStr = localStorage.getItem(USER_INFO_KEY);
  if (userInfoStr) {
    try {
      return JSON.parse(userInfoStr);
    } catch (e) {
      return null;
    }
  }
  return null;
}

// 请求拦截器
request.interceptors.request.use(
  config => {
    // 添加Token到请求头
    const token = getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    console.log('发送请求:', config.method?.toUpperCase(), config.url, config.data);
    return config;
  },
  error => {
    console.error('请求拦截器错误:', error);
    return Promise.reject(error);
  }
);

// 响应拦截器
request.interceptors.response.use(
  response => {
    console.log('收到响应:', response.config.method?.toUpperCase(), response.config.url, response.status, response.data);
    const res = response.data;
    
    // 如果是 Blob 类型（文件下载），直接返回
    if (res instanceof Blob) {
      return res;
    }
    
    if (res.code === 200 || res.code === 201) {
      return res;
    } else {
      console.error('响应错误:', res);
      return Promise.reject(new Error(res.message || '请求失败'));
    }
  },
  async error => {
    console.error('请求错误:', error);
    console.error('错误详情:', {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
      config: error.config
    });
    
    // 处理401错误（Token过期或无效）
    if (error.response?.status === 401) {
      const originalRequest = error.config;
      
      // 如果是登录接口，直接返回错误
      if (originalRequest.url === '/auth/login') {
        const errorMessage = error.response?.data?.message || '用户名或密码错误';
        return Promise.reject(new Error(errorMessage));
      }
      
      // 尝试使用刷新Token获取新Token
      const refreshTokenValue = getRefreshToken();
      if (refreshTokenValue && !originalRequest._retry) {
        originalRequest._retry = true;
        
        try {
          const response = await refreshToken(refreshTokenValue);
          if (response.code === 200 && response.data.token) {
            // 更新Token
            setToken(response.data.token, refreshTokenValue, response.data.user);
            
            // 重试原始请求
            originalRequest.headers.Authorization = `Bearer ${response.data.token}`;
            return request(originalRequest);
          }
        } catch (refreshError) {
          // 刷新Token失败，清除登录状态并跳转到登录页
          clearToken();
          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
          return Promise.reject(new Error('登录已过期，请重新登录'));
        }
      } else {
        // 没有刷新Token或已经重试过，清除登录状态并跳转到登录页
        clearToken();
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
        return Promise.reject(new Error('登录已过期，请重新登录'));
      }
    }
    
    // 提取后端返回的错误信息
    const errorMessage = error.response?.data?.message || error.message || '请求失败';
    return Promise.reject(new Error(errorMessage));
  }
);

export default request;

